import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import CircuitParameterConstant from '../../../components/CellInfo/CircuitParameterConstant';

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#fff', // Softer, modern background
    padding: 40,
    fontSize: 12,
    color: '#222',
    fontFamily: 'Helvetica', // Use modern sans-serif if available
    lineHeight: 1.5,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottom: '2 solid #e8442d',
    paddingBottom: 12,
    paddingTop: 12,
    marginBottom: 32,
    justifyContent: 'space-between',
    // backgroundColor: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 8px #e8442d22',
    paddingHorizontal: 24,
  },
  titleBlock: {
    flex: 1,
    textAlign: 'center',
  },
  title: {
    fontSize: 24,
    color: '#e8442d',
    fontWeight: 'bold',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  company: {
    fontSize: 14,
    color: '#888',
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: 2,
    marginBottom: 2,
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 15,
    color: '#888',
    fontStyle: 'italic',
    marginTop: 6,
    letterSpacing: 1,
  },
  section: {
    marginBottom: 28,
  },
  sectionTitle: {
    color: '#e8442d',
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 18,
    marginTop: 20,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    borderLeft: '4 solid #e8442d',
    paddingLeft: 10,
  },
  sectionTitleTable: {
    color: '#e8442d',
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 18,
    marginTop: 18,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    borderLeft: '4 solid #e8442d',
    paddingLeft: 10,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  label: {
    color: '#e8442d',
    fontWeight: 'bold',
    width: '40%',
    fontSize: 13,
    letterSpacing: 0.5,
  },
  value: {
    color: '#222',
    fontWeight: 500,
    width: '60%',
    fontSize: 13,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 20,
    marginBottom: 32,
    gap: 24,
  },
  statCard: {
    backgroundColor: '#fff',
    border: '1.5 solid #e8442d',
    borderRadius: 16,
    padding: 22,
    minWidth: 130,
    alignItems: 'center',
    marginHorizontal: 8,
    boxShadow: '0 2px 8px #e8442d22',
  },
  statLabel: {
    color: '#e8442d',
    fontWeight: 'bold',
    fontSize: 14,
    marginBottom: 6,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  statValue: {
    color: '#222',
    fontWeight: 'bold',
    fontSize: 22,
    letterSpacing: 1,
  },
  imageRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginBottom: 32,
    gap: 32,
  },
  cellImage: {
    width: 120,
    height: 120,
    objectFit: 'cover',
    border: '1.5 solid #e8442d',
    borderRadius: 12,
    marginRight: 16,
    boxShadow: '0 2px 8px #e8442d22',
  },
  barcode: {
    width: 150,
    height: 150,
    objectFit: 'contain',
    borderRadius: 12,
    // backgroundColor: '#fff',
    padding: 4,
    boxShadow: '0 2px 8px #e8442d22',
  },
  table: {
    display: 'table',
    width: 'auto',
    marginBottom: 18,
    border: '1.5 solid #e8442d',
    borderRadius: 8,
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px #e8442d22',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #e8442d',
    backgroundColor: '#f7f8fa', // alternate row color will be set inline
  },
  tableHeader: {
    backgroundColor: '#e8442d',
    color: '#fff',
    padding: 8,
    fontSize: 13,
    flex: 1,
    textAlign: 'center',
    borderRight: '1 solid #e8442d',
    letterSpacing: 1,
  },
  tableCell: {
    padding: 8,
    fontSize: 12,
    flex: 1,
    textAlign: 'center',
    borderRight: '1 solid #e8442d',
    color: '#222',
  },
  barCell: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
    borderRight: '1 solid #e8442d',
  },
  barBg: {
    width: 40,
    height: 14,
    backgroundColor: '#eee',
    borderRadius: 7,
    border: '1 solid #bbb',
    position: 'relative',
    margin: '0 auto',
  },
  barFg: {
    height: 14,
    borderRadius: 7,
    position: 'absolute',
    left: 0,
    top: 0,
  },
  plotImage: {
    width: 370,
    height: 200,
    objectFit: 'contain',
    marginVertical: 16,
    alignSelf: 'center',
    border: '1.5 solid #e8442d',
    borderRadius: 12,
    boxShadow: '0 2px 8px #e8442d22',
  },
  placeholder: {
    color: '#aaa',
    fontStyle: 'italic',
    textAlign: 'center',
    marginVertical: 14,
  },
  footer: {
    position: 'absolute',
    bottom: 40,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingTop: 12,
    paddingBottom: 0,
    paddingHorizontal: 0,
    backgroundColor: 'transparent',
  },
  signatureBlock: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  signature: {
    marginTop: 10,
    fontSize: 12,
    color: '#888',
    fontStyle: 'italic',
    marginBottom: 0,
  },
  date: {
    fontSize: 11,
    color: '#888',
    fontWeight: 500,
  },
  logo: {
    width: 54,
    height: 54,
    objectFit: 'contain',
    padding: 4,
    borderRadius: 12,
    boxShadow: '0 2px 8px #e8442d22',
    // backgroundColor: '#fff',
  },
  cellImagesRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    // marginBottom: 32,
    gap: 24,
    // backgroundColor: '#fff',
    // borderRadius: 12,
    // boxShadow: '0 2px 8px #e8442d22',
    padding: 12,
  },
  cellDemoImage: {
    width: 150,
    height: 200,
    objectFit: 'cover',
    borderRadius: 12,
    backgroundColor: '#fff',
    border: '1.5 solid #e8442d',
    marginHorizontal: 8,
    boxShadow: '0 2px 8px #e8442d22',
  },
  circuitImage: {
    width: 500,
    height: 200,
    objectFit: 'contain',
    alignSelf: 'center',
    marginBottom: 16,
    marginTop: 16,
    borderRadius: 12,
    backgroundColor: '#fff',
    border: '1.5 solid #e8442d',
    boxShadow: '0 2px 8px #e8442d22',
  },
});

const getExplanation = (param) => {
  const found = CircuitParameterConstant.find(c => c.name === param);
  return found ? found.explanation : '';
};
const getUnit = (param) => {
  const found = CircuitParameterConstant.find(c => c.name === param);
  return found ? found.unit : '';
};

// Add min, max, typical, and explanation for LG 21700 NMC cell
const circuitParamRanges = {
    r0:    { value: 0.0042, min: 0.002, max: 0.01, typical: 0.0035, unit: 'Ohm', explanation: 'Electrolyte resistance' },
    r1:    { value: 0.012,  min: 0.002, max: 0.05, typical: 0.015, unit: 'Ohm', explanation: 'SEI layer resistance' },
    c1:    { value: 0.0028, min: 0.001, max: 0.01, typical: 0.003, unit: 'Farad', explanation: 'SEI layer capacitance' },
    r2:    { value: 0.018,  min: 0.005, max: 0.05, typical: 0.02, unit: 'Ohm', explanation: 'Charge-transfer resistance' },
    wo1:   { value: 0.012,  min: 0.001, max: 0.05, typical: 0.01, unit: '-', explanation: 'Warburg impedance' },
    c2:    { value: 0.093,  min: 0.01,  max: 0.5,  typical: 0.1, unit: 'Farad', explanation: 'Double-layer capacitance' },
  };  

const getBarColor = (percentage) => {
  if (percentage < 30) return '#4CAF50'; // Green for good
  if (percentage < 70) return '#FF9800'; // Orange for moderate
  return '#F44336'; // Red for high
};

const CellHealthCertificatePDF = ({
  cellData = {},
  circuitParameters = {},
  soh = '',
  risk = '',
  rul = '',
  cellImage = '',
  barcode = '',
  plotImage = '',
  impedanceImage = '',
  hppcImage = '',
  chargeDischargeImage = '',
  date = new Date().toLocaleDateString(),
  recipient = 'Cell Owner',
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Image src={'https://scope.thinkclock.com/assets/Tab_Icon.png'} style={styles.logo} />
        <View style={styles.titleBlock}>
          <Text style={styles.title}>Cell Health Certificate</Text>
          {/* <Text style={styles.company}>ThinkClock Battery Labs</Text> */}
          <Text style={styles.subtitle}>Comprehensive Analysis Report</Text>
        </View>
      </View>

      {/* Images Row */}
      {/* <Text style={{ color: '#e8442d', fontWeight: 'bold', fontSize: 13, marginBottom: 4, textAlign: 'center' }}>Cell Images:</Text> */}
      <View style={styles.cellImagesRow}>
        <Image src={'https://scope.thinkclock.com/assets/cert/cellCertimg1.jpeg'} style={styles.cellDemoImage} />
        <Image src={'https://scope.thinkclock.com/assets/cert/cellCertimg2.jpeg'} style={styles.cellDemoImage} />
        <Image src={'https://scope.thinkclock.com/assets/cert/cellCertimg3.jpeg'} style={styles.cellDemoImage} />
      </View>

      {/* Cell Info Section */}
      <Text style={styles.sectionTitle}>Cell Information</Text>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <View style={{ flex: 1 }}>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Make/Model:</Text>
            <Text style={styles.value}>LG 21700</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Nominal Capacity:</Text>
            <Text style={styles.value}>5000 mAh</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Current Capacity:</Text>
            <Text style={styles.value}>4500 mAh</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Chemistry:</Text>
            <Text style={styles.value}>NMC</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Nominal Voltage:</Text>
            <Text style={styles.value}>3.6 V</Text>
          </View>
        </View>
        {barcode ? (
          <Image src={barcode} style={styles.barcode} />
        ) : null}
      </View>

      {/* Stats Section */}
      <Text style={styles.sectionTitle}>Key Metrics</Text>
      <View style={styles.statsRow}>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>SOH</Text>
          <Text style={styles.statValue}>90%</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>Risk Score</Text>
          <Text style={styles.statValue}> 4/10</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>RUL</Text>
          <Text style={styles.statValue}>575 Cycles</Text>
        </View>
      </View>
      <View break />

      {/* Cycle Plot Section */}
      <Text style={styles.sectionTitle}>Capacity vs Cycle</Text>
      <Image src={'https://scope.thinkclock.com/assets/cert/plots/capvscycle.png'} style={{ width: '100%', height: 220, objectFit: 'contain', marginVertical: 12, alignSelf: 'center' }} />

      <Text style={styles.sectionTitle}>Internal Resistance vs Cycle</Text>
      <Image src={'https://scope.thinkclock.com/assets/cert/plots/irvscycle.png'} style={{ width: '100%', height: 220, objectFit: 'contain', marginVertical: 12, alignSelf: 'center' }} />

      {/* add page break */}
      <View break />
      
      {/* Charge/Discharge Graph Section */}
      <Text style={styles.sectionTitle}>Charge/Discharge Graphs</Text>
      <Image src={'https://scope.thinkclock.com/assets/cert/plots/chargedisplot.png'} style={{ width: '100%', height: 220, objectFit: 'contain', marginVertical: 12, alignSelf: 'center' }} />

      {/* Circuit Parameters Table */}
      <Text style={styles.sectionTitle}>Equivalent Circuit Diagram</Text>
      <Image src={'https://scope.thinkclock.com/assets/equivalentcircuit.png'} style={styles.circuitImage} />
      <View break />
      <Text style={styles.sectionTitleTable}>Equivalent Circuit Model Parameters</Text>
      {Object.keys(circuitParamRanges).length > 0 ? (
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={styles.tableHeader}>Parameter</Text>
            <Text style={styles.tableHeader}>Value</Text>
            {/* <Text style={styles.tableHeader}>Min</Text>
            <Text style={styles.tableHeader}>Max</Text> */}
            <Text style={styles.tableHeader}>Typical</Text>
            <Text style={styles.tableHeader}>Unit</Text>
            {/* <Text style={styles.tableHeader}>Bar</Text> */}
            <Text style={styles.tableHeader}>Explanation</Text>
          </View>
          {Object.entries(circuitParamRanges).map(([param, range], idx) => {
            const min = range.min;
            const max = range.max;
            const typical = range.typical;
            const unit = range.unit;
            const val = range.value;
            const percent = (val - min) / (max - min) * 100;
            const barPercent = Math.max(0, Math.min(100, percent));
            const barColor = getBarColor(barPercent);
            return (
              <View style={{...styles.tableRow, backgroundColor: idx % 2 === 0 ? '#fff' : '#f7f8fa'}} key={param}>
                <Text style={styles.tableCell}>{param.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Text>
                <Text style={styles.tableCell}>{val.toFixed(4)}</Text>
                {/* <Text style={styles.tableCell}>{min}</Text>
                <Text style={styles.tableCell}>{max}</Text> */}
                <Text style={styles.tableCell}>{typical}</Text>
                <Text style={styles.tableCell}>{unit}</Text>
                {/* <View style={styles.barCell}>
                  <View style={styles.barBg}>
                    <View style={{ ...styles.barFg, width: `${barPercent}%`, backgroundColor: barColor }} />
                  </View>
                </View> */}
                <Text style={styles.tableCell}>{range.explanation}</Text>
              </View>
            );
          })}
        </View>
      ) : (
        <Text style={styles.placeholder}>No circuit parameters available.</Text>
      )}

      {/* Footer/Signature */}
      <View style={styles.footer}>
        <Text style={styles.date}>Date: {date}</Text>
        <View style={styles.signatureBlock}>
          <Text>Authorized by</Text>
          <Text style={styles.signature}>ThinkClock Battery Analytics Team</Text>
        </View>
      </View>
    </Page>
  </Document>
);

export default CellHealthCertificatePDF; 